import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

export type ErrorContext = {
  workspaceId?: string;
  projectId?: string;
  scanId?: string;
  userId?: string;
  [key: string]: any;
}

export const reportError = (
  error: Error,
  context?: ErrorContext,
  options?: {
    captureInSentry?: boolean;
    captureInPostHog?: boolean;
    logToConsole?: boolean;
  }
) => {
  const {
    captureInSentry = true,
    captureInPostHog = true,
    logToConsole = true,
  } = options || {};

  if (logToConsole && process.env.NODE_ENV !== "production") {
    console.error("Error reported:", error, context);
  }

  if (captureInSentry) {
    Sentry.captureException(error, {
      extra: context,
      tags: {
        workspaceId: context?.workspaceId,
        projectId: context?.projectId,
        scanId: context?.scanId,
      },
    });
  }

  if (captureInPostHog) {
    posthog.captureException(error, context);
  }
};

export const raiseError = (
  message: string,
  context?: ErrorContext,
  options?: {
    captureInSentry?: boolean;
    captureInPostHog?: boolean;
    logToConsole?: boolean;
    throwError?: boolean;
  }
): Error => {
  const {
    captureInSentry = true,
    captureInPostHog = true,
    logToConsole = true,
    throwError = true,
  } = options || {};

  const error = new Error(message);

  reportError(error, context, {
    captureInSentry,
    captureInPostHog,
    logToConsole,
  });

  if (throwError) {
    throw error;
  }

  return error;
};

export const setupGlobalErrorHandlers = () => {
  window.addEventListener("error", (event) => {
    reportError(event.error || new Error(event.message), {
      type: "uncaught_error",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    reportError(error, {
      type: "unhandled_promise_rejection",
    });
  });
};
